import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetcher } from '@/shared/api';
import { policyKeys } from './queryKeys';
import type {
  PolicyDetailResponse,
  PolicyListResponse,
  PolicyAcceptancesResponse,
  PolicyVersionsResponse,
} from '../model/types';

function buildQs(params: Record<string, unknown>): string {
  return new URLSearchParams(
    Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''),
    ) as Record<string, string>,
  ).toString();
}

/**
 * Fetch the current policy that requires user acceptance
 * Don't retry on 404 - it means no policy is pending
 * Uses merchant-prefixed endpoint: GET /merchant/policies/current
 * This is a @Public() endpoint - no auth required, but hasAccepted populated if token sent
 * 
 * @param enabled - set false to defer the fetch until needed (e.g. modal not yet open)
 * 
 * **Validates: Requirements 1, 18**
 */
export function useCurrentPolicy(
  documentType: 'PRIVACY_POLICY' | 'TERMS_AND_CONDITIONS' = 'PRIVACY_POLICY',
  enabled = true,
) {
  return useQuery({
    queryKey: policyKeys.current(documentType),
    queryFn: () =>
      fetcher<PolicyDetailResponse>(
        `/api/v2/merchant/policies/current?documentType=${documentType}`,
      ),
    enabled,
    retry: (failureCount, error: unknown) => {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 404) return false;
      return failureCount < 1;
    },
  });
}

/**
 * Fetch list of all policies with optional filters
 * 
 * **Validates: Requirements 6, 18**
 */
export function usePolicyList(filters?: Record<string, unknown>) {
  const query = filters ? buildQs(filters) : '';
  return useQuery({
    queryKey: policyKeys.list(filters ?? {}),
    queryFn: () =>
      fetcher<PolicyListResponse>(
        `/api/v2/system/policies${query ? `?${query}` : ''}`,
      ),
  });
}

/**
 * Fetch detailed information for a specific policy
 * 
 * **Validates: Requirements 13, 16, 18**
 */
export function usePolicyDetail(id: string) {
  return useQuery({
    queryKey: policyKeys.detail(id),
    queryFn: () => fetcher<PolicyDetailResponse>(`/api/v2/system/policies/${id}`),
    enabled: !!id,
  });
}

/**
 * Fetch acceptance audit log for a policy with pagination
 * 
 * **Validates: Requirements 11, 18**
 */
export function usePolicyAcceptances(
  id: string,
  params?: { page?: number; pageSize?: number },
) {
  const query = params ? buildQs(params) : '';
  return useQuery({
    queryKey: policyKeys.acceptances(id, params ?? {}),
    queryFn: () =>
      fetcher<PolicyAcceptancesResponse>(
        `/api/v2/system/policies/${id}/acceptances${query ? `?${query}` : ''}`,
      ),
    enabled: !!id,
  });
}

/**
 * Fetch version history for a policy.
 * The server has no dedicated /versions endpoint — the list endpoint
 * at /policies returns every version (draft, published, archived).
 * This hook reads that cached data and returns it in PolicyVersionsResponse shape,
 * avoiding a redundant network call.
 * 
 * **Validates: Requirements 16, 18**
 */
export function usePolicyVersions(_id: string, documentType: string) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: policyKeys.versions(_id),
    queryFn: () => {
      // Read from the filtered list cache first — no extra request needed
      const cached = queryClient.getQueryData<PolicyListResponse>(
        policyKeys.list({ documentType })
      );
      if (cached) {
        return Promise.resolve({ success: true, data: cached.data } as PolicyVersionsResponse);
      }
      // Cache miss: fetch the list directly with documentType filter
      return fetcher<PolicyVersionsResponse>(
        `/api/v2/system/policies?documentType=${documentType}`,
      );
    },
    enabled: !!_id,
  });
}
